import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, PartyPopper } from "lucide-react";
import { GuideMascot } from "../components/GuideMascot";
import { PillLink } from "../components/common";
import { CONTACT, GUIDES, PROGRAMS, BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="font-body font-600 text-sm text-[#1E293B]">{label}{required && <span className="text-[#E76F81]"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls =
  "w-full rounded-2xl border-2 border-[#e2e8f0] bg-white px-4 py-3 font-body text-[#1E293B] outline-none transition-colors duration-200 focus:border-[#4A90E2] focus:ring-4 focus:ring-[#FFD166]/40 placeholder:text-[#94a3b8]";

const Enroll = () => {
  const [form, setForm] = useState({
    parent_name: "", email: "", phone: "", child_name: "", child_age: "", program: "", schedule: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Your inquiry has been sent! We'll be in touch soon. 🌿");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="enroll-page">
      <section className="bg-[#FDFBF7] pt-14 pb-12">
        <div className="mx-auto max-w-5xl px-5 md:px-8 text-center">
          <div className="flex justify-center mb-4"><GuideMascot src={GUIDES.wave} size={130} ring="#FFD166" className="animate-bob" /></div>
          <span className="font-hand text-2xl text-[#4A90E2]">We'd love to meet your little one</span>
          <h1 className="font-head font-700 tracking-tight text-4xl sm:text-5xl text-[#1E293B] mt-1">Enroll & Connect</h1>
          <p className="mt-4 font-body text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Tell us a little about your family and we'll reach out to talk about the perfect fit at Divinities Cove.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-5 gap-8 -mt-4">
          {/* Form */}
          <div className="lg:col-span-3">
            {done ? (
              <div className="rounded-[2rem] bg-[#eef4ef] p-10 text-center shadow-soft" data-testid="enroll-success">
                <div className="flex justify-center"><PartyPopper size={56} className="text-[#84A98C]" /></div>
                <h2 className="mt-4 font-head font-700 text-3xl text-[#1E293B]">Thank you! 🌿</h2>
                <p className="mt-3 font-body text-[#475569] max-w-md mx-auto">
                  Your inquiry has been received. Our team will reach out to you soon. We can't wait to welcome your little soul to the cove!
                </p>
                <p className="mt-6 font-hand text-3xl text-[#4A90E2]">{BRAND.tagline}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-[2rem] bg-[#FDFBF7] p-7 md:p-10 shadow-soft border border-black/5 space-y-5" data-testid="enroll-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Parent / Guardian Name" required>
                    <input data-testid="input-parent-name" className={inputCls} value={form.parent_name} onChange={upd("parent_name")} placeholder="Your full name" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={upd("phone")} placeholder="(000) 000-0000" />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={upd("email")} placeholder="you@example.com" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Child's Name">
                    <input data-testid="input-child-name" className={inputCls} value={form.child_name} onChange={upd("child_name")} placeholder="Little one's name" />
                  </Field>
                  <Field label="Child's Age">
                    <input data-testid="input-child-age" className={inputCls} value={form.child_age} onChange={upd("child_age")} placeholder="e.g. 4 years" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Program of Interest">
                    <select data-testid="select-program" className={inputCls} value={form.program} onChange={upd("program")}>
                      <option value="">Select a program…</option>
                      {PROGRAMS.map((p) => <option key={p.title} value={p.title}>{p.title}</option>)}
                    </select>
                  </Field>
                  <Field label="Schedule Needed">
                    <select data-testid="select-schedule" className={inputCls} value={form.schedule} onChange={upd("schedule")}>
                      <option value="">Select a schedule…</option>
                      <option>Full-Time</option>
                      <option>Part-Time</option>
                      <option>Drop-In</option>
                      <option>After-School</option>
                      <option>Saturday</option>
                      <option>Not sure yet</option>
                    </select>
                  </Field>
                </div>
                <Field label="Anything you'd like us to know?">
                  <textarea data-testid="input-message" rows={4} className={inputCls} value={form.message} onChange={upd("message")} placeholder="Share your child's interests, questions, or anything else…" />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enroll-submit-btn"
                  className="inline-flex items-center justify-center gap-2 font-head font-600 text-base px-8 py-4 rounded-full bg-[#4A90E2] text-white shadow-soft hover:bg-[#2f6fbf] hover:-translate-y-0.5 transition-[transform,background-color] duration-200 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? "Sending…" : <>Send Inquiry <Send size={18} /></>}
                </button>
              </form>
            )}
          </div>

          {/* Contact / hours */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-[2rem] bg-[#eaf2fc] p-8 shadow-soft-sm" data-testid="contact-card">
              <h3 className="font-head font-600 text-xl text-[#1E293B]">Get in touch</h3>
              <ul className="mt-4 space-y-4 font-body text-[#475569]">
                <li className="flex items-start gap-3"><Phone size={20} className="text-[#4A90E2] mt-0.5" /><span>{CONTACT.phone}</span></li>
                {CONTACT.email && <li className="flex items-start gap-3"><Mail size={20} className="text-[#4A90E2] mt-0.5" /><span>{CONTACT.email}</span></li>}
                {CONTACT.address && <li className="flex items-start gap-3"><MapPin size={20} className="text-[#4A90E2] mt-0.5" /><span>{CONTACT.address}</span></li>}
              </ul>
            </div>
            <div className="rounded-[2rem] bg-[#eef4ef] p-8 shadow-soft-sm">
              <h3 className="font-head font-600 text-xl text-[#1E293B] flex items-center gap-2"><Clock size={20} className="text-[#5f8468]" /> Hours</h3>
              <ul className="mt-4 space-y-3 font-body">
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between gap-3 border-b border-black/5 pb-2 last:border-0">
                    <span className="font-600 text-[#1E293B]">{h.day}</span>
                    <span className="text-[#475569] text-right">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[2rem] bg-[#FFD166] p-8 shadow-soft-sm flex items-center gap-4">
              <GuideMascot src={GUIDES.point} size={90} ring="#ffffff" />
              <p className="font-hand text-2xl text-[#1E293B]">Questions? We're always happy to chat!</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enroll;
